import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { AuthForm, AuthMode } from '@/components/auth/AuthForm';
import { Loader2, Sparkles, Check } from 'lucide-react';

const TRIAL_PROMO_CODE = 'bookit3';

interface PromoInfo {
  valid: boolean;
  kind?: 'static' | 'reader';
  readerName?: string;
  readerFirstName?: string;
  readerPhoto?: string | null;
  readerRate?: number | null;
  trialDays?: number;
  description?: string;
}

const TrialPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuthContext();

  const initialEmail = searchParams.get('email') || '';
  const promoParam = (searchParams.get('promo') || '').toLowerCase().trim();
  const [mode, setMode] = useState<AuthMode>('register');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  // Reader referral codes (?promo=katie) get a booking-first funnel layout.
  // Invalid or missing codes fall back to the default bookit3 3-day trial.
  const [promoInfo, setPromoInfo] = useState<PromoInfo | null>(null);
  const [promoChecked, setPromoChecked] = useState(!promoParam);
  const [showFunnel, setShowFunnel] = useState(false);

  useEffect(() => {
    if (!promoParam) return;
    fetch(`/api/subscription/promo-info/${encodeURIComponent(promoParam)}`)
      .then(r => r.json())
      .then((info: PromoInfo) => setPromoInfo(info?.valid ? info : null))
      .catch(() => setPromoInfo(null))
      .finally(() => setPromoChecked(true));
  }, [promoParam]);

  const isReaderPromo = !!(promoInfo?.valid && promoInfo.kind === 'reader');
  const activeCode = promoInfo?.valid ? promoParam : TRIAL_PROMO_CODE;

  const redeemTrial = async () => {
    setIsRedeeming(true);
    setRedeemError(null);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const res = await fetch('/api/subscription/redeem-promo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ promoCode: activeCode }),
      });

      const data = await res.json();

      if (res.ok && data.checkoutUrl) {
        // Mark welcome as dismissed so it doesn't show after checkout
        localStorage.setItem('heyjoe_welcome_dismissed', 'true');
        window.location.href = data.checkoutUrl;
        return;
      }

      // Already subscribed or already redeemed — send them home
      if (data.error?.includes('already')) {
        navigate('/');
        return;
      }

      setRedeemError(data.error || 'Something went wrong');
    } catch {
      setRedeemError('Failed to start trial. Please try again.');
    } finally {
      setIsRedeeming(false);
    }
  };

  // If already logged in, go straight to trial redemption — but wait for the
  // promo lookup to settle so we redeem the right code.
  useEffect(() => {
    if (isAuthenticated && promoChecked) {
      redeemTrial();
    }
  }, [isAuthenticated, promoChecked]);

  const handleAuthSuccess = (token: string, user: any) => {
    login(token, user);
    // The useEffect above will fire and redeem the trial
  };

  if (isAuthenticated) {
    return (
      <main
        className="flex items-center justify-center px-4"
      // Subtract the global header (68px) so the centered card stays in the
      // remaining viewport instead of forcing a small scroll.
        style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: 'calc(100vh - 68px)' }}
      >
        <div className="text-center space-y-4">
          {redeemError ? (
            <>
              <p className="m-0" style={{ fontSize: 14, color: 'hsl(var(--destructive))' }}>
                {redeemError}
              </p>
              <button
                onClick={() => navigate('/')}
                style={{
                  background: 'transparent',
                  border: 0,
                  color: 'var(--hj-ink-3)',
                  textDecoration: 'underline',
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Go to homepage
              </button>
            </>
          ) : (
            <>
              <Loader2 className="h-8 w-8 animate-spin mx-auto" style={{ color: 'var(--hj-yellow-ink)' }} />
              <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-ink-3)' }}>
                Starting your free trial…
              </p>
            </>
          )}
        </div>
      </main>
    );
  }

  // Hold rendering until we know whether this is a reader referral —
  // prevents the default layout flashing in before the funnel.
  if (promoParam && !promoChecked) {
    return (
      <main
        className="flex items-center justify-center px-4"
        style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: 'calc(100vh - 68px)' }}
      >
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />
      </main>
    );
  }

  // ── Reader referral funnel — "Book {reader}, the platform's included" ──
  if (isReaderPromo) {
    const first = promoInfo?.readerFirstName || 'your reader';
    const rate = typeof promoInfo?.readerRate === 'number' ? promoInfo.readerRate : null;
    const steps = ['Create account', 'Add card — $0 today', `Book ${first}`];

    return (
      <main
        className="flex items-center justify-center px-4"
        style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: 'calc(100vh - 68px)' }}
      >
        <div className="w-full" style={{ maxWidth: 980, padding: '40px 0' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Pitch — the trade, spelled out */}
            <div className="text-center lg:text-left">
              {promoInfo?.readerPhoto && (
                <img
                  src={promoInfo.readerPhoto}
                  alt={promoInfo.readerName || 'Your reader'}
                  className="mx-auto lg:mx-0 object-cover"
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: 5,
                    border: '2px solid var(--hj-hairline)',
                    marginBottom: 18,
                  }}
                />
              )}
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--hj-ink)' }}>
                {promoInfo?.readerName}
                <span style={{ color: 'var(--hj-ink-2)', fontWeight: 500 }}> · Professional reader</span>
              </div>
              {rate !== null && (
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--hj-ink)', marginTop: 2 }}>
                  ${rate} / 15-min session
                </div>
              )}
              <h1
                className="font-hj-display m-0"
                style={{
                  marginTop: 16,
                  fontSize: 'clamp(30px, 3.8vw, 44px)',
                  lineHeight: 1.05,
                  letterSpacing: '-0.03em',
                  fontWeight: 800,
                  color: 'var(--hj-ink)',
                }}
              >
                Book {first} — get a month of Hey Joe Plus{' '}
                <em style={{ color: 'var(--hj-yellow-ink)' }}>free</em>.
              </h1>
              <p
                className="m-0"
                style={{ marginTop: 16, fontSize: 15.5, lineHeight: 1.65, color: 'var(--hj-ink)' }}
              >
                A 15-minute session with {first} runs {rate !== null ? `$${rate}` : 'their listed rate'}.
                Book through this link and your first month of Hey Joe Plus — normally $9.99 — comes
                with it, free: 10 video tokens, studio-grade audio cleanup, and the
                Audition Enhancer to polish the tape you make together.
              </p>
            </div>

            {/* Funnel — Book Now → account → $0 checkout → booking form */}
            <div
              style={{
                padding: '26px 28px',
                borderRadius: 'var(--hj-r-md)',
                background: 'var(--hj-surface-1)',
                border: '1px solid var(--hj-hairline)',
              }}
            >
              <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1" style={{ marginBottom: 20 }}>
                {steps.map((s, i) => (
                  <React.Fragment key={s}>
                    {i > 0 && <span style={{ color: 'var(--hj-ink-4)' }}>→</span>}
                    <span
                      style={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: showFunnel && i === 0 ? 'var(--hj-blue)' : 'var(--hj-ink)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {i + 1}. {s}
                    </span>
                  </React.Fragment>
                ))}
              </div>

              {!showFunnel ? (
                <>
                  <button
                    type="button"
                    onClick={() => setShowFunnel(true)}
                    className="w-full"
                    style={{
                      padding: '16px 24px',
                      borderRadius: 999,
                      background: 'var(--hj-blue)',
                      border: '1px solid var(--hj-blue)',
                      color: 'oklch(0.18 0.005 70)',
                      fontSize: 17,
                      fontWeight: 700,
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    Book Now
                  </button>
                  <p
                    className="text-center m-0"
                    style={{ marginTop: 14, fontSize: 13, fontWeight: 500, color: 'var(--hj-ink)' }}
                  >
                    $0 today · 30 days of Plus free, then $9.99/month · cancel anytime
                  </p>
                </>
              ) : (
                <>
                  <p
                    className="text-center m-0"
                    style={{ marginBottom: 14, fontSize: 13.5, fontWeight: 600, color: 'var(--hj-ink)' }}
                  >
                    Create your account to book {first}
                  </p>
                  <AuthForm
                    mode={mode}
                    onModeChange={setMode}
                    onSuccess={handleAuthSuccess}
                    initialEmail={initialEmail}
                    compact
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    );
  }

  // ── Default trial page (bookit3) ──
  const features = [
    'Professional video tools',
    '10 video tokens',
    '5 reader bookings',
  ];

  return (
    <main
      className="flex items-center justify-center px-4"
      // Subtract the global header (68px) so the centered card stays in the
      // remaining viewport instead of forcing a small scroll.
      style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: 'calc(100vh - 68px)' }}
    >
      <div className="w-full" style={{ maxWidth: 440 }}>
        <div className="text-center" style={{ marginBottom: 24 }}>
          <div
            className="mx-auto flex items-center justify-center"
            style={{
              width: 56,
              height: 56,
              borderRadius: 999,
              background: 'var(--hj-blue-tint)',
              color: 'var(--hj-yellow-ink)',
              marginBottom: 16,
            }}
          >
            <Sparkles className="h-7 w-7" />
          </div>
          <div className="mono-label" style={{ marginBottom: 8, color: 'var(--hj-ink-3)' }}>3-day trial</div>
          <h1
            className="font-hj-display m-0"
            style={{
              fontSize: 'clamp(32px, 5vw, 44px)',
              lineHeight: 1,
              color: 'var(--hj-ink)',
              letterSpacing: '-0.03em',
              fontWeight: 800,
            }}
          >
            Try the whole kit.
          </h1>
          <p
            className="m-0"
            style={{
              marginTop: 12,
              color: 'var(--hj-ink)',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            3 days of Hey Joe Plus — free. No charge until the trial ends.
          </p>
        </div>

        {/* Feature list */}
        <div
          style={{
            padding: 16,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
            marginBottom: 16,
          }}
        >
          {features.map((feature) => (
            <div key={feature} className="flex items-center gap-2" style={{ padding: '5px 0', fontSize: 14.5, fontWeight: 500, color: 'var(--hj-ink)' }}>
              <Check className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--hj-green)' }} />
              <span>{feature}</span>
            </div>
          ))}
          <div
            style={{
              paddingTop: 10,
              marginTop: 10,
              borderTop: '1px solid var(--hj-hairline-soft)',
              textAlign: 'center',
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--hj-ink)',
            }}
          >
            3 days free, then $9.99/month · cancel anytime
          </div>
        </div>

        {/* Auth form */}
        <div
          style={{
            padding: 24,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
          }}
        >
          <p
            className="text-center m-0"
            style={{ marginBottom: 14, fontSize: 13, color: 'var(--hj-ink-2)', fontWeight: 500 }}
          >
            {mode === 'login' ? 'Sign in to start your trial' : 'Create an account to get started'}
          </p>
          <AuthForm
            mode={mode}
            onModeChange={setMode}
            onSuccess={handleAuthSuccess}
            initialEmail={initialEmail}
            compact
          />
        </div>
      </div>
    </main>
  );
};

export default TrialPage;
